import { SignUpForm, SignUpRequest } from "@/ServerTypes/Membership";
import { SignupAccountType } from "@/ServerTypes/Membership/SignupAccountType";
import { PropertyPanel } from "@serenity-is/corelib";
import { information, isEmptyOrNull, resolveUrl, serviceCall, localText } from "@serenity-is/corelib/q";
import { SignUpResponse } from "@/ServerTypes/Membership/SignUpResponse";

$(function () {
    new SignUpPanel($('#SignUpPanel'));
});

/**
 * Two-step account creation, per mockup screens 3 and 4.
 *
 * Step 1 is the role choice - it sets AccountType, which decides both the record created
 * (Customer vs EndUsers) and the role granted (Portal User vs Portal Customer). Step 2 is the
 * role-specific form; the two roles differ because Customer stores one combined
 * Customer_City_St_Zip column while EndUsers has separate City/State/Zip columns.
 *
 * The wording deliberately never says "Customer" to mean dealer, even though the dealer table
 * is called Customer.
 */
class SignUpPanel extends PropertyPanel<SignUpRequest, any> {

    protected getFormKey() { return SignUpForm.formKey; }

    private form: SignUpForm;
    private selectedRole: SignupAccountType = null;

    constructor(container: JQuery) {
        super(container);

        this.form = new SignUpForm(this.idPrefix);

        this.form.ConfirmEmail.addValidationRule(this.uniqueName, e => {
            if (this.form.ConfirmEmail.value !== this.form.Email.value) {
                return localText('Validation.EmailConfirm');
            }
        });

        // WI 36112. Mirrors Curbtender.Common.Helpers.SignupUsername on the server - the server
        // check is the one that counts, this only saves a round trip. Kept as one message so the
        // two cannot describe the rule differently.
        this.form.Username.addValidationRule(this.uniqueName, e => {
            var username = (this.form.Username.value ?? '').trim();
            if (isEmptyOrNull(username))
                return localText('Validation.Required');

            if (!/^[A-Za-z][A-Za-z0-9._]{2,99}$/.test(username)) {
                return 'User names must start with a letter and can contain letters, numbers, ' +
                    'dots and underscores - no spaces or @ signs. Minimum 3 characters.';
            }
        });

        this.form.ConfirmPassword.addValidationRule(this.uniqueName, e => {
            if (this.form.ConfirmPassword.value !== this.form.Password.value) {
                // 'Validation.PasswordConfirm' does not exist in the text registry - localText
                // falls back to returning the key, so a mismatch used to display the literal
                // string "Validation.PasswordConfirm" to the user. The real key is
                // Validation.PasswordConfirmMismatch.
                return localText('Validation.PasswordConfirmMismatch');
            }
        });

        // City/State/Zip (customer) and CityStateZip (dealer) are mutually exclusive, so each is
        // only required for the role that actually persists it.
        this.form.City.addValidationRule(this.uniqueName, e => {
            if (this.isDealer()) return null;
            if (isEmptyOrNull(this.form.City.value)) return localText('Validation.Required');
        });
        this.form.State.addValidationRule(this.uniqueName, e => {
            if (this.isDealer()) return null;
            if (isEmptyOrNull(this.form.State.value)) return localText('Validation.Required');
        });
        this.form.Zip.addValidationRule(this.uniqueName, e => {
            if (this.isDealer()) return null;
            if (isEmptyOrNull(this.form.Zip.value)) return localText('Validation.Required');
        });
        this.form.CityStateZip.addValidationRule(this.uniqueName, e => {
            if (!this.isDealer()) return null;
            if (isEmptyOrNull(this.form.CityStateZip.value)) return localText('Validation.Required');
        });

        this.element.find('.choice-card').click(e => {
            var role = $(e.currentTarget).data('role') === 'dealer'
                ? SignupAccountType.Dealer
                : SignupAccountType.Customer;

            this.selectRole(role);
        });

        this.byId('ContinueButton').click(e => {
            e.preventDefault();
            if (this.selectedRole === null)
                return;

            this.showStep(2);
        });

        this.byId('BackToLoginButton').click(e => {
            e.preventDefault();
            window.location.href = resolveUrl('~/Account/Login');
        });

        this.byId('BackToRoleButton').click(e => {
            e.preventDefault();
            this.showStep(1);
        });

        this.byId('SubmitButton').click(e => {
            e.preventDefault();
            this.submit();
        });

        this.showStep(1);
    }

    private selectRole(role: SignupAccountType) {
        this.selectedRole = role;
        this.form.AccountType.value = role.toString();

        var isDealer = role === SignupAccountType.Dealer;
        this.element.find('.choice-card').removeClass('sel');
        this.element.find('.choice-card[data-role="' + (isDealer ? 'dealer' : 'customer') + '"]').addClass('sel');
        this.byId('ContinueButton').prop('disabled', false);
    }

    private isDealer() {
        return this.selectedRole === SignupAccountType.Dealer;
    }

    private showStep(step: number) {
        this.byId('Step1').toggle(step === 1);
        this.byId('Step2').toggle(step === 2);

        if (step !== 2)
            return;

        var isDealer = this.isDealer();
        this.byId('RoleBadge').text(isDealer ? 'Dealer or distributor' : 'Truck owner / operator');

        this.form.CityStateZip.element.closest('.field').toggle(isDealer);
        this.form.City.element.closest('.field').toggle(!isDealer);
        this.form.State.element.closest('.field').toggle(!isDealer);
        this.form.Zip.element.closest('.field').toggle(!isDealer);
    }

    private submit() {
        if (!this.validateForm())
            return;

        serviceCall({
            url: resolveUrl('~/Account/SignUp'),
            request: {
                ContactName: this.form.ContactName.value,
                Email: this.form.Email.value,
                Username: this.form.Username.value,
                Password: this.form.Password.value,
                AccountType: this.form.AccountType.value,
                CompanyName: this.form.CompanyName.value,
                Phone: this.form.Phone.value,
                Address: this.form.Address.value,
                City: this.form.City.value,
                State: this.form.State.value,
                Zip: this.form.Zip.value,
                CityStateZip: this.form.CityStateZip.value
            },
            onSuccess: (response: SignUpResponse) => {
                // A Close-tier match sends the visitor to the claim picker (screen 5) so they can
                // confirm which record is theirs, rather than the system deciding for them. The
                // token carries the pending signup - see PendingSignupToken.
                if (response.NeedsClaimReview) {
                    window.location.href = resolveUrl('~/Account/SignUp/Review?t=')
                        + encodeURIComponent(response.ClaimToken);
                    return;
                }

                // Exact/None: the server has already signed this session in (screen 4), so go
                // straight to whichever dashboard their role resolves to.
                window.location.href = resolveUrl('~/');
            }
        });
    }

    getTemplate() {
        return `<div class="signup-wrap">
    <div class="text-center signup-brand">
        <img src="${resolveUrl("~/Content/site/images/curbtenderlogo.png")}" width="140" height="32" alt="Curbtender" />
    </div>

    <div id="~_Step1" class="s-Panel p-4">
        <div class="signup-head">
            <h2>Create your account</h2>
            <p>Step 1 of 2 &mdash; which best describes you?</p>
        </div>
        <div class="choice">
            <div class="choice-card" data-role="dealer">
                <h4>I'm a dealer or distributor</h4>
                <p>My company <b>sells and services</b> Curbtender equipment.</p>
                <ul>
                    <li>See the trucks you've sold</li>
                    <li>See the customers who own them</li>
                    <li>Track warranty registrations and service cases</li>
                </ul>
            </div>
            <div class="choice-card" data-role="customer">
                <h4>I own or operate a truck</h4>
                <p>My organization <b>uses</b> Curbtender equipment.</p>
                <ul>
                    <li>See your own trucks and warranty status</li>
                    <li>Register warranty and request service</li>
                    <li>Follow progress on your open cases</li>
                </ul>
            </div>
        </div>
        <div class="signup-actions">
            <button id="~_ContinueButton" type="button" class="btn btn-primary" disabled>Continue</button>
            <button id="~_BackToLoginButton" type="button" class="btn btn-outline-secondary">Back</button>
        </div>
    </div>

    <div id="~_Step2" class="s-Panel p-4">
        <div class="signup-head">
            <h2>Create your account</h2>
            <p>Step 2 of 2 &mdash; <span id="~_RoleBadge" class="signup-badge"></span></p>
        </div>
        <form id="~_Form" action="">
            <div id="~_PropertyGrid"></div>
            <div class="signup-actions">
                <button id="~_SubmitButton" type="submit" class="btn btn-primary">Create account</button>
                <button id="~_BackToRoleButton" type="button" class="btn btn-outline-secondary">Back</button>
            </div>
        </form>
    </div>
</div>`;
    }
}
